import * as React from 'react';

import { Autocomplete, Box, Button, Checkbox, FormControl, FormControlLabel, InputLabel, OutlinedInput, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { DataGrid, GridColDef, GridRowParams, GridRenderCellParams } from '@mui/x-data-grid';
import { Interface, InterfaceProperty, FieldType, getInterfaceProperties, InterfacePropFieldTypeStrings } from '../../types/Interface';
import { copyArrayWithIndexChanged } from '../../Utils';
import { AlertProps } from '@mui/material/Alert';
import { useSnackbar } from '../../../contexts/SnackBarContext';
import { TestCombination, TestComboSupportVal, getTestComboSupport, updateTestComboSupport, deleteTestComboSupport } from '../../types/TestCombinations';

import { httpPost } from "../../Web";
import { ApiResponse } from "../../types/ApiResponse";

const PropertyColumns: GridColDef[] = [{
        field: 'isValid',
        headerName: 'Valid',
        flex: 1,
        renderCell: (params: GridRenderCellParams<any, boolean>) => {
            if (params.row.isValid === false) {
                return <CloseIcon color={'error'} />
            }
            if (params.row.isValid === true) {
                return <CheckIcon color={'success'} />
            }
            return <RemoveCircleOutlineIcon />
        },
    },
    {
        field: 'group',
        headerName: 'Group',
        flex: 2
    },
    {
        field: 'name',
        headerName: 'Name',
        flex: 3,
    },
    {
        field: 'fieldType',
        headerName: 'Field Type',
        flex: 1,
        valueFormatter: (value?: number) => {
            if (value == null) {
                return '';
            }
            return InterfacePropFieldTypeStrings[value];
        }
    },
    {
        field: 'fieldSchema',
        headerName: 'Field Schema',
        flex: 1
    },
    {
        field: 'id',
        headerName: 'ID',
        flex: 1
    }
];

export const GetEmptyIfaceProp = (): InterfaceProperty => ({
    id: 0,
    interfaceId: 0,
    group: '',
    name: '',
    fieldType: FieldType.CheckBox,
    fieldSchema: ''
});

interface RowValue extends InterfaceProperty {
    isValid?: boolean; // This is used to indicate if the TestCase is valid for the selected test combination
};

const getRowValues = (iFaceProps: InterfaceProperty[], allSupportedCombo: TestComboSupportVal[]): RowValue[] => {
    if (iFaceProps.length === 0) {
        return [];
    }
    let rowvalues: RowValue[] = [];
    iFaceProps.forEach(ifp => {
        const isValid = allSupportedCombo.some(combo => combo.interfacePropertyId === ifp.id && combo.testCombinationId !== undefined);
        const value: RowValue = { ...ifp, isValid: isValid };
        rowvalues.push(value);
    });
    return rowvalues;
}

const updateInterfaceProperty = (data: InterfaceProperty, abortController: AbortController): Promise<ApiResponse<InterfaceProperty>> => httpPost(`/api/interface/property/update`, data, abortController);

export const PropertiesAdmin = ({ selectedTestCombination, selectedInterface, selectedIFaceProp, setSelectedIFaceProp }:
    {
        selectedTestCombination: TestCombination, selectedInterface: Interface, selectedIFaceProp: InterfaceProperty,
        setSelectedIFaceProp: (value: React.SetStateAction<InterfaceProperty>) => void
    }) => {
    const { showSnackbar } = useSnackbar();
    const [allIFaceProps, setAllIFaceProps] = React.useState<InterfaceProperty[]>([]);
    const [rowvalues, setRowvalues] = React.useState<RowValue[]>([]);
    const [allSupportedCombo, setAllSupportedCombo] = React.useState<TestComboSupportVal[]>([]);
    const [selectedCombo, setselectedCombo] = React.useState<TestComboSupportVal>({});
    const [alteredCombo, setAlteredCombo] = React.useState<TestComboSupportVal>({});

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        const fetchTestCombinations = async () => {
            if (!selectedInterface || selectedInterface.id === undefined) {
                setAllIFaceProps([]);
                setRowvalues([]);
                return;
            };
            const allIFacePropsResponse = await getInterfaceProperties(selectedInterface.id, new AbortController());
            if (allIFacePropsResponse.failed) {
                showUserFeedback(allIFacePropsResponse.errorText);
                return;
            }
            setAllIFaceProps(allIFacePropsResponse.result);
            setRowvalues(getRowValues(allIFacePropsResponse.result, []));
        }
        fetchTestCombinations();
    }, [showUserFeedback, selectedInterface]);
        
    React.useEffect(() => {
        const fetchSupportedCombinations = async () => {
            if (!selectedTestCombination || selectedTestCombination.id === undefined || !selectedInterface || selectedInterface.id === undefined) {
                setAllSupportedCombo([]);
                setRowvalues(getRowValues(allIFaceProps, []));
                return;
            }

            const testComboSupport: TestComboSupportVal = {
                testCombinationId: selectedTestCombination.id,
                interfaceId: selectedInterface.id,
                interfacePropertyId: undefined, // all properties will be fetched
                testCaseId: 0 // 0 means not applicable
            }
            const allcombosResponse = await getTestComboSupport(testComboSupport, new AbortController());
            if (allcombosResponse.failed) {
                showUserFeedback(allcombosResponse.errorText);
                return;
            }
            setAllSupportedCombo(allcombosResponse.result);
            setRowvalues(getRowValues(allIFaceProps, allcombosResponse.result));
            setSelectedIFaceProp(GetEmptyIfaceProp());
            setselectedCombo({});
            setAlteredCombo({});
        }
        fetchSupportedCombinations();
    }, [showUserFeedback, setSelectedIFaceProp, selectedTestCombination, allIFaceProps, selectedInterface]);

    const saveHandler = async (iFacePropData: InterfaceProperty) => {
        let isNewEntry = false;

        if (iFacePropData.interfaceId === undefined || iFacePropData.interfaceId === 0) {
            iFacePropData.interfaceId = selectedInterface.id;
            isNewEntry = true;
        }

        const updatediFaceProp = await updateInterfaceProperty(iFacePropData, new AbortController());
        if (updatediFaceProp.failed) {
            showUserFeedback(updatediFaceProp.errorText);
            return;
        }
        const updatediFacePropLoc = updatediFaceProp.result;
        setSelectedIFaceProp(GetEmptyIfaceProp());
        let allIFacePropsLoc = copyArrayWithIndexChanged(allIFaceProps, allIFaceProps.findIndex(tc => tc.id === updatediFacePropLoc.id), updatediFacePropLoc);
        setAllIFaceProps(allIFacePropsLoc);

        if (selectedTestCombination.id !== undefined && selectedTestCombination.id > 0) {
            if (selectedCombo.interfacePropertyId !== alteredCombo.interfacePropertyId) {
                if (isNewEntry) {
                    alteredCombo.interfacePropertyId = updatediFacePropLoc.id;
                }
                if (alteredCombo.interfacePropertyId) {
                    updateTestComboSupport(alteredCombo, new AbortController()).then((response) => {
                        if (response.failed) {
                            showUserFeedback(response.errorText);
                            return;
                        }
                        const updatedCombo = response.result;
                        const allSupportedComboLoc = copyArrayWithIndexChanged(allSupportedCombo, allSupportedCombo.findIndex(combo => combo.id === updatedCombo.id), updatedCombo);
                        setAllSupportedCombo(allSupportedComboLoc);
                    });
                }
                else if (selectedCombo.id !== undefined) {
                    deleteTestComboSupport(selectedCombo.id ?? 0, new AbortController()).then((response) => {
                        if (response.failed) {
                            showUserFeedback(response.errorText);
                            return;
                        }
                        const updatedCombo = response.result;
                        const allSupportedComboLoc = allSupportedCombo.filter(combo => combo.id !== updatedCombo.id);
                        setAllSupportedCombo(allSupportedComboLoc);
                    });
                }
                setselectedCombo({});
                setAlteredCombo({});
            }
        }
    }
    
    const onSubmitHandler = (e: { preventDefault: () => void; }) => {
        e.preventDefault();
        saveHandler(selectedIFaceProp);
    }

    const onSelectionChange = (sel: GridRowParams) => {
        if (sel.id === selectedIFaceProp.id) {
            setSelectedIFaceProp(GetEmptyIfaceProp()); // deselect if already selected
            setselectedCombo({});
            setAlteredCombo({});
            return;
        }
        setSelectedIFaceProp(allIFaceProps.find(ifp => ifp.id === sel.id) as InterfaceProperty);
        const selCombo: TestComboSupportVal = allSupportedCombo.find(combo => combo.interfacePropertyId === sel.id) ?? {};
        setselectedCombo(selCombo);
        setAlteredCombo(selCombo);
    }

    return <React.Fragment>
        <Typography variant='h4' paddingBottom={10}>
            {selectedTestCombination.id && selectedInterface.id ? 'Configure Interface Properties for ' +
                selectedInterface.name + ' of ' + selectedTestCombination.combinationName
                : 'Interface Properties Settings. Please select a Test Combination & Interface to configure valid Interface Properties.'}
        </Typography>
        <Box sx={{ height: '80%', width: '100%' }}>
            <Grid container spacing={2} alignItems={'center'} >
                <Grid size={9} paddingBottom={10}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel shrink htmlFor='group'>{"Group"}</InputLabel>
                        <OutlinedInput notched name='group' id='group'
                            label={"Group"} value={selectedIFaceProp?.group}
                            onChange={(e) => setSelectedIFaceProp({ ...selectedIFaceProp, group: e.target.value })}
                        />
                    </FormControl>
                </Grid>
                <Grid size={1} />
                <Grid size={2} paddingBottom={10}>
                    <FormControlLabel control={<Checkbox
                        checked={alteredCombo.interfacePropertyId !== undefined ? true : false}
                        inputProps={{ 'aria-label': 'controlled' }}
                        onChange={(e) => setAlteredCombo(e.target.checked ? { interfacePropertyId: selectedIFaceProp.id, interfaceId: selectedInterface.id, testCombinationId: selectedTestCombination.id } : {})} />}
                        label="Valid"
                    />
                </Grid>
                <Grid size={9} paddingBottom={10}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel shrink htmlFor='name'>{"Name"}</InputLabel>
                        <OutlinedInput notched name='name' id='name'
                            label={"Name"} value={selectedIFaceProp?.name}
                            onChange={(e) => setSelectedIFaceProp({ ...selectedIFaceProp, name: e.target.value })}
                        />
                    </FormControl>
                </Grid>
                <Grid size={3} paddingBottom={10}>
                    <FormControl required fullWidth>
                        <Autocomplete id='tcft' options={InterfacePropFieldTypeStrings}
                            value={InterfacePropFieldTypeStrings[(selectedIFaceProp?.fieldType) ?? 2]}
                            onChange={(e, value) => setSelectedIFaceProp({ ...selectedIFaceProp, fieldType: InterfacePropFieldTypeStrings.indexOf(value as string) })}
                            renderInput={(params) =>
                                <TextField name='tcft' multiline
                                    {...params}
                                    label={"Field Type"}
                                    sx={{
                                        '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                                            borderColor: 'divider',
                                        },
                                    }}
                                />
                            }
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth variant="outlined">
                        <InputLabel shrink htmlFor='fieldSchema'>{"Field Schema"}</InputLabel>
                        <OutlinedInput notched name='fieldSchema' id='fieldSchema'
                            label={"Field Schema"} value={selectedIFaceProp?.fieldSchema}
                            onChange={(e) => setSelectedIFaceProp({ ...selectedIFaceProp, fieldSchema: e.target.value })}
                        />
                    </FormControl>
                </Grid>
                <Grid size={1} />
                <Grid size={2}>
                    <Button fullWidth startIcon={<ClearIcon />}
                        onClick={() => {
                            setSelectedIFaceProp(GetEmptyIfaceProp());
                        }}>
                        New
                    </Button>
                </Grid>
                <Grid size={1} />
                <Grid size={2} marginRight='0px' marginLeft='auto'>
                    <Button fullWidth startIcon={<SaveIcon />}
                        onClick={(e) => {
                            onSubmitHandler(e);
                        }}>
                        Save
                    </Button>
                </Grid>
            </Grid>
            <Box sx={{ width: '100%' }} marginTop={15}>
                <DataGrid
                    columns={PropertyColumns}
                    rows={rowvalues}
                    columnVisibilityModel={{ id: false }}
                    onRowClick={onSelectionChange}
                    getRowClassName={(params) => params.row.id === selectedIFaceProp.id ? 'selectRowClass' : ''}
                    hideFooter
                    sx={{
                        "& .selectRowClass": {
                            color: 'primary.main'
                        },
                    }}
                />
            </Box>
        </Box>
    </React.Fragment>
}
